/**
 * Cora Upsell: Cart Recommendations
 * Fetches complementary product recommendations for items in the cart.
 * Subscribes to FoxTheme PubSub for cart updates.
 */
(function () {
  'use strict';

  const isDev =
    window.location.hostname === 'localhost' ||
    new URLSearchParams(window.location.search).has('preview_theme_id');

  window.CoraUpsell = window.CoraUpsell || {};

  if (customElements.get('cora-cart-recommendations')) return;

  class CoraCartRecommendations extends HTMLElement {
    constructor() {
      super();
      this._cartUnsubscribe = null;
      this._lastProductId = null;
      this._adding = new Set(); // track per-variantId so only one request per variant in-flight
    }

    connectedCallback() {
      this._lastProductId = null; // reset on reconnect so same product re-fetches
      if (window.requestIdleCallback) {
        requestIdleCallback(() => this._init());
      } else {
        setTimeout(() => this._init(), 200);
      }
    }

    disconnectedCallback() {
      if (this._cartUnsubscribe) {
        this._cartUnsubscribe();
        this._cartUnsubscribe = null;
      }
    }

    _init() {
      if (!window.FoxTheme || !FoxTheme.pubsub) return;
      if (this._cartUnsubscribe) return; // already subscribed — prevent double-subscribe on reconnect

      this._cartUnsubscribe = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        (event) => this._onCartUpdate(event)
      );

      // Initial load from server-rendered cart data
      this._loadFromCurrentCart();
    }

    _loadFromCurrentCart() {
      fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
        .then((r) => {
          if (!r.ok) throw new Error('Cart fetch failed');
          return r.json();
        })
        .then((cart) => this._processCart(cart))
        .catch((e) => {
          if (isDev) console.error('[CoraCartRecs] _loadFromCurrentCart failed:', e);
        });
    }

    _onCartUpdate(event) {
      if (!event) return;
      if (event.cart) {
        // Use the cart payload carried in the event to avoid a redundant fetch
        this._processCart(event.cart);
      } else {
        // Event fired without a cart payload (e.g. external cart mutation) —
        // fall back to fetching the current cart state
        this._loadFromCurrentCart();
      }
    }

    // I3: A/B variant assignment for the seed-item selection strategy.
    // Persisted to sessionStorage so the same customer keeps the same variant
    // across page loads in their session — mixing variants mid-session would
    // pollute the measurement. Falls back to in-memory storage in Safari
    // Private Mode where sessionStorage throws on write.
    _getRecsVariant() {
      const KEY = '_cora_recs_variant';
      try {
        let v = sessionStorage.getItem(KEY);
        if (v === 'A' || v === 'B') return v;
        // First touch in this session: 50/50 split.
        v = Math.random() < 0.5 ? 'A' : 'B';
        sessionStorage.setItem(KEY, v);
        return v;
      } catch (_e) {
        // Safari Private Mode / disabled storage. Fall back to per-instance
        // memory; not session-stable but doesn't break the customer flow.
        if (!this._memoryVariant) {
          this._memoryVariant = Math.random() < 0.5 ? 'A' : 'B';
        }
        return this._memoryVariant;
      }
    }

    // I3: Pick the seed cart item to ask for complementary recs around.
    //   Variant A (control): highest original_price item — implicitly assumes
    //     "biggest item = strongest signal of intent".
    //   Variant B (treatment): most recently added item (last in cart.items[]
    //     per Shopify's insertion-order convention) — assumes "most recent
    //     click = freshest intent". Same-product re-fetch dedup still applies.
    _pickSeedItem(cart, variant) {
      if (variant === 'B') {
        // cart.items[] is insertion-ordered; last entry is the most recent add.
        return cart.items[cart.items.length - 1];
      }
      // Variant A: highest-priced item (existing behaviour).
      let top = cart.items[0];
      for (const item of cart.items) {
        if (item.original_price > top.original_price) top = item;
      }
      return top;
    }

    _processCart(cart) {
      if (!cart || !cart.items || cart.items.length === 0) {
        this._clear();
        return;
      }

      const variant = this._getRecsVariant();
      const topItem = this._pickSeedItem(cart, variant);

      // Don't re-fetch if same product. Note: variant change WITHIN a session
      // is impossible because variant is sticky (sessionStorage), so this dedup
      // never accidentally suppresses a variant transition.
      if (this._lastProductId === topItem.product_id) return;
      this._lastProductId = topItem.product_id;
      this._lastVariant = variant;

      // Get product IDs in cart to filter them out
      const cartProductIds = new Set(cart.items.map((item) => item.product_id));

      this._fetchRecommendations(topItem.product_id, cartProductIds);
    }

    _fetchRecommendations(productId, cartProductIds) {
      const limit = parseInt(this.dataset.limit, 10) || 4;
      const url =
        '/recommendations/products.json?product_id=' +
        productId +
        '&limit=' +
        (limit + cartProductIds.size) +
        '&intent=complementary';

      fetch(url, { priority: 'low' })
        .then((r) => r.json())
        .then((data) => {
          if (!data.products || data.products.length === 0) {
            this._clear();
            return;
          }

          // Filter out products already in cart
          const filtered = data.products.filter((p) => !cartProductIds.has(p.id));
          const toShow = filtered.slice(0, limit);

          if (toShow.length === 0) {
            this._clear();
            return;
          }

          this._render(toShow);
        })
        .catch((e) => {
          if (isDev) console.error('[CoraCartRecs] fetch recommendations failed:', e);
          this._clear();
        });
    }

    _render(products) {
      const container = this.querySelector('.cora-cart-recs__list');
      if (!container) return;

      // Clear existing items
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }

      products.forEach((product) => {
        const item = this._createProductCard(product);
        container.appendChild(item);
      });

      this.hidden = false;

      // I3: Push the variant + payload to GA4 / GTM dataLayer once per
      // distinct render. Lets us measure click-through and conversion lift
      // (or regression) of variant B vs control. Guarded so missing
      // dataLayer (e.g. SPA test harness) doesn't throw.
      try {
        if (window.dataLayer && typeof window.dataLayer.push === 'function') {
          window.dataLayer.push({
            event: 'cart_recs_rendered',
            cart_recs_variant: this._lastVariant || 'A',
            cart_recs_seed_product_id: this._lastProductId,
            cart_recs_count: products.length,
          });
        }
      } catch (_e) {
        /* analytics push must not break the render */
      }
    }

    _createProductCard(product) {
      const li = document.createElement('li');
      li.className = 'cora-cart-recs__item';

      const image = product.featured_image || (product.images && product.images[0]);
      const variant = product.variants && product.variants[0];
      const price = variant ? variant.price : 0;
      const variantId = variant ? variant.id : null;
      const isAvailable = variant ? variant.available === true : false;

      // Image
      const imgWrap = document.createElement('div');
      imgWrap.className = 'cora-cart-recs__image';
      if (image) {
        const imgEl = document.createElement('img');
        const rawSrc = typeof image === 'string' ? image : image.src || image.url || '';
        imgEl.src = rawSrc.replace(/(\.[^.?]+)(\?|$)/, '_120x$1$2');
        imgEl.alt = product.title;
        imgEl.loading = 'lazy';
        imgEl.width = 56;
        imgEl.height = 56;
        imgWrap.appendChild(imgEl);
      }
      li.appendChild(imgWrap);

      // Info
      const info = document.createElement('div');
      info.className = 'cora-cart-recs__info';

      const title = document.createElement('a');
      title.className = 'cora-cart-recs__title';
      title.href = '/products/' + encodeURIComponent(product.handle);
      title.textContent = product.title;
      info.appendChild(title);

      const priceEl = document.createElement('div');
      priceEl.className = 'cora-cart-recs__price';
      priceEl.textContent = this._formatMoney(price);
      info.appendChild(priceEl);

      li.appendChild(info);

      // Add button
      if (variantId) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cora-cart-recs__add-btn';
        if (isAvailable) {
          btn.textContent = 'Add';
          btn.setAttribute('aria-label', 'Add ' + product.title + ' to cart');
          btn.addEventListener('click', () => this._addToCart(variantId, btn));
        } else {
          btn.textContent = 'Sold out';
          btn.disabled = true;
          btn.setAttribute('aria-label', product.title + ' is sold out');
          btn.classList.add('cora-cart-recs__add-btn--soldout');
        }
        li.appendChild(btn);
      }

      return li;
    }

    _addToCart(variantId, btn) {
      if (this._adding.has(variantId)) return;
      this._adding.add(variantId);
      btn.classList.add('cora-cart-recs__add-btn--adding');
      btn.textContent = 'Adding...';
      btn.disabled = true;

      const body = JSON.stringify({ items: [{ id: variantId, quantity: 1 }] });

      return fetch('/cart/add.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
      })
        .then((r) => {
          if (!r.ok) throw new Error('Add failed');
          return fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
        })
        .then((r) => {
          if (!r.ok) throw new Error('Cart fetch failed');
          return r.json();
        })
        .then((cart) => {
          // Fetch sections for cart drawer update
          const sections = [];
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', {
              bubbles: true,
              detail: { sections: sections },
            })
          );

          if (sections.length > 0) {
            return fetch('/cart?sections=' + sections.join(','), {
              headers: { Accept: 'application/json' },
            })
              .then((r) => {
                if (!r.ok) throw new Error('Sections fetch failed');
                return r.json();
              })
              .then((sectionData) => {
                cart.sections = sectionData;
                return cart;
              });
          }
          return cart;
        })
        .then((cart) => {
          if (btn.isConnected) {
            btn.classList.remove('cora-cart-recs__add-btn--adding');
            btn.textContent = 'Added';
            btn.disabled = false;
            setTimeout(() => {
              if (btn.isConnected) btn.textContent = 'Add';
            }, 2000);
          }
          if (window.FoxTheme && FoxTheme.pubsub) {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              cart: cart,
              source: 'cora-cart-recs',
            });
          }
        })
        .catch(() => {
          if (btn.isConnected) {
            btn.classList.remove('cora-cart-recs__add-btn--adding');
            btn.textContent = 'Failed';
            btn.disabled = false;
            setTimeout(() => {
              if (btn.isConnected) btn.textContent = 'Add';
            }, 2000);
          }
          if (window.CartDebug) console.warn('[CoraCartRecs] Add to cart failed');
          // If the add succeeded but the cart.js fetch failed, the cart is stale.
          // Attempt a fresh cart fetch so the UI reflects server state.
          fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
            .then((r) => {
              if (!r.ok) throw new Error('Cart fetch failed');
              return r.json();
            })
            .then((cart) => {
              if (window.FoxTheme && FoxTheme.pubsub) {
                FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                  cart: cart,
                  source: 'cora-cart-recs',
                });
              }
            })
            .catch((e) => {
              if (isDev) console.error('[CoraCartRecs] add to cart failed:', e);
            });
        })
        .finally(() => {
          this._adding.delete(variantId);
        });
    }

    _clear() {
      const container = this.querySelector('.cora-cart-recs__list');
      if (container) {
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }
      }
      this.hidden = true;
      this._lastProductId = null;
    }

    _formatMoney(cents) {
      const amount = (parseFloat(cents) / 100 || 0).toFixed(2);
      return window.Shopify && Shopify.currency && Shopify.currency.active
        ? '$' + amount + ' ' + Shopify.currency.active
        : '$' + amount;
    }
  }

  customElements.define('cora-cart-recommendations', CoraCartRecommendations);
})();
